import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2,
  FiX, FiUpload, FiDownload, FiTrash2,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import './OrderDetail.css';
import ProductProgressStrip from '../components/ProductProgressStrip';
import OrderTrackingIndicator from '../components/OrderTrackingIndicator';

/* ─── helpers ──────────────────────────────────────────────────────── */

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const STATUS_META = {
  draft:     { label: 'Draft',     color: '#6b7280', bg: '#f3f4f6' },
  confirmed: { label: 'Confirmed', color: '#059669', bg: '#d1fae5' },
  cancelled: { label: 'Cancelled', color: '#ef4444', bg: '#fee2e2' },
  delivered: { label: 'Delivered', color: '#2563eb', bg: '#dbeafe' },
};

const parseDeliveryAddress = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return { line1: raw }; }
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

/* ─── sub-components ────────────────────────────────────────────────── */

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span className="od-badge" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

/* ─── Production Progress ─────────────────────────────────────────── */

const DesignerLog = ({ order, onSaved }) => (
  <div className="od-section">
    <div className="od-section-header">
      <div className="od-section-title">
        🖨️ Production Progress
        <span className="od-section-sub">
          Hover connectors between steps to insert · click outsource badge for purchase link
        </span>
      </div>
    </div>
    <ProductProgressStrip
      order={order}
      onOrderUpdate={(updatedOrder) => {
        onSaved && onSaved(updatedOrder.processEntries || order.processEntries);
      }}
    />
  </div>
);

/* ─── File helpers ─────────────────────────────────────────────────── */

const FILE_ICONS = {
  cdr:  { icon: '🎨', color: '#059669' },
  psd:  { icon: '🖼️', color: '#2563eb' },
  ai:   { icon: '✏️', color: '#f59e0b' },
  pdf:  { icon: '📄', color: '#ef4444' },
  svg:  { icon: '🔷', color: '#7c3aed' },
  png:  { icon: '🖼️', color: '#2563eb' },
  jpg:  { icon: '🖼️', color: '#2563eb' },
  jpeg: { icon: '🖼️', color: '#2563eb' },
  eps:  { icon: '📐', color: '#059669' },
  tiff: { icon: '🖼️', color: '#6b7280' },
  tif:  { icon: '🖼️', color: '#6b7280' },
  indd: { icon: '📰', color: '#ef4444' },
  fig:  { icon: '🔷', color: '#7c3aed' },
  xd:   { icon: '🎨', color: '#f59e0b' },
};

const getFileIcon = (filename = '') => {
  const ext = filename.split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || { icon: '📎', color: '#6b7280' };
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACCEPT = '.cdr,.psd,.ai,.pdf,.svg,.png,.jpg,.jpeg,.webp,.gif,.eps,.tiff,.tif,.indd,.xd,.fig';

/* ─── Client-side version detection (no upload needed) ──────────────── */

const CDR_RIFF_VERSION_MAP = {
  '3': 'v3', '4': 'v4', '5': 'v5', '6': 'v6', '7': 'v7',
  '8': 'v8', '9': 'v9', A: 'v10', B: 'v11', C: 'v12',
  D: 'v13', E: 'v14', F: 'X5',
};

const AI_VERSION_MAP = {
  1:'v1',2:'v2',3:'v3',4:'v4',5:'v5',6:'v6',7:'v7',8:'v8',9:'v9',
  10:'CS',11:'CS2',12:'CS3',13:'CS4',14:'CS5',15:'CS5.5',16:'CS6',
  17:'CC',18:'CC 2014',19:'CC 2015',20:'CC 2017',21:'CC 2018',
  22:'CC 2019',23:'2020',24:'2021',25:'2022',26:'2023',27:'2024',28:'2025',
};

const INDD_VERSION_MAP = {
  1:'1.0',2:'2.0',3:'3.0',4:'4.0',5:'5.0',6:'CS',7:'CS2',8:'CS3',
  9:'CS4',10:'CS5',11:'CS5.5',12:'CS6',13:'CC',14:'CC 2014',
  15:'CC 2015',16:'CC 2017',17:'CC 2018',18:'CC 2019',19:'2020',
  20:'2021',21:'2022',22:'2023',23:'2024',
};

// CoreVersion raw values → File version from official Corel documentation
const CDR_CORE_VERSION_MAP = {
  0:    '0.0',  100: '1.0',  200: '2.0',  300: '3.0',  400: '4.0',
  500:  '5.0',  600: '6.0',  700: '7.0',  800: '8.0',  900: '9.0',
  1000: '10.0', 1100: '11.0', 1200: '12.0', 1300: '13.0', 1400: '14.0',
  1500: '15.0', 1600: '16.0', 1700: '17.0', 1800: '18.0', 1900: '19.0',
  2000: '20.0', 2100: '21.0', 2200: '22.0', 2300: '23.0', 2400: '24.0',
  2500: '25.0', 2600: '26.0', 2700: '27.0',
};

const detectFileVersionClient = async (file) => {
  const ext = file.name.split('.').pop().toLowerCase();

  // Only these three formats have detectable versions
  if (!['cdr', 'ai', 'indd'].includes(ext)) return null;

  try {
    // For AI we need 2 KB; for INDD 12 bytes; CDR needs full file for ZIP
    const sliceSize = ext === 'ai' ? 2048 : ext === 'indd' ? 12 : 64 * 1024;
    const buffer = await file.slice(0, sliceSize).arrayBuffer();
    const bytes  = new Uint8Array(buffer);

    // ── INDD ────────────────────────────────────────────────────
    if (ext === 'indd') {
      const magic = [0x06,0x06,0xED,0xF5,0xD8,0x1D,0x46,0xE5];
      if (bytes.length < 12) return null;
      if (!magic.every((b, i) => bytes[i] === b)) return null;
      const view  = new DataView(buffer);
      const major = view.getUint32(8, false) >> 16;
      return INDD_VERSION_MAP[major] ? `InDesign ${INDD_VERSION_MAP[major]}` : `InDesign v${major}`;
    }

    // ── AI ──────────────────────────────────────────────────────
    if (ext === 'ai') {
      const header = new TextDecoder('latin1').decode(bytes);
      let m = header.match(/%%AIVersion:\s*([\d.]+)/);
      if (!m) m = header.match(/Adobe Illustrator(?:\(R\))?\s+([\d.]+)/i);
      if (!m) return null;
      const major = parseInt(m[1], 10);
      const name  = AI_VERSION_MAP[major];
      return name ? `Illustrator ${name}` : `Illustrator v${m[1]}`;
    }

    // ── CDR ─────────────────────────────────────────────────────
    if (ext === 'cdr') {
      const ascii = (start, len) =>
        Array.from(bytes.slice(start, start + len))
          .map((b) => String.fromCharCode(b))
          .join('');

      // FORMAT A — RIFF (older): first 4 bytes = "RIFF", bytes 8-10 = "CDR"
      if (ascii(0, 4) === 'RIFF' && ascii(8, 3) === 'CDR') {
        const vc = ascii(11, 1);
        return `CorelDRAW ${CDR_RIFF_VERSION_MAP[vc] || `(char: ${vc})`}`;
      }

      // FORMAT B — ZIP-based (X6+): PK magic → use JSZip to read metadata
      if (bytes[0] === 0x50 && bytes[1] === 0x4B) {
        const zip = await JSZip.loadAsync(await file.arrayBuffer());

        // Confirm it's a CorelDRAW ZIP
        const mimeEntry = zip.file('mimetype');
        if (mimeEntry) {
          const mime = await mimeEntry.async('text');
          if (!mime.includes('corel')) return null;
        }

        // Primary: META-INF/metadata.xml → <cdr:CoreVersion>
        const metaEntry = zip.file('META-INF/metadata.xml');
        if (metaEntry) {
          const xml = await metaEntry.async('text');
          const m = xml.match(/<cdr:CoreVersion>(\d+)<\/cdr:CoreVersion>/);
          if (m) {
            const cv   = parseInt(m[1], 10);
            const name = CDR_CORE_VERSION_MAP[cv];
            return name ? `CorelDRAW Version ${name}` : `CorelDRAW v${cv}`;
          }
        }

        // Fallback: content.xml <meta:generator>
        const contentEntry = zip.file('content.xml') || zip.file('Content.xml');
        if (contentEntry) {
          const xml = await contentEntry.async('text');
          const m = xml.match(/CorelDRAW\s+(X\d+|\d{4}(?:\.\d+)?)/i);
          if (m) return `CorelDRAW ${m[1]}`;
        }

        return 'CorelDRAW X6+';
      }
    }
  } catch {
    // Silently ignore — version detection is best-effort
  }

  return null;
};

/* ─── Design Files Drawer ──────────────────────────────────────────── */

const DesignFilesDrawer = ({ line, designFiles, onClose, onUpload, onDelete, uploading }) => {
  const [dragOver, setDragOver]       = useState(false);
  const [pendingFile, setPendingFile]  = useState(null);
  const inputRef = useRef(null);

  const lineId = line.id;
  const files  = designFiles.filter((f) => f.productLineId === lineId);

  const stageFile = async (file) => {
    setPendingFile({ file, version: null, detecting: true });
    const version = await detectFileVersionClient(file);
    setPendingFile({ file, version, detecting: false });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) stageFile(file);
  };

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) stageFile(file);
    e.target.value = '';
  };

  const handleConfirmUpload = () => {
    if (!pendingFile) return;
    onUpload(lineId, line.product, {
      target: { files: [pendingFile.file], value: '' },
    });
  };

  const handleCancelPending = () => {
    setPendingFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  useEffect(() => {
    if (!uploading) setPendingFile(null);
  }, [uploading]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (pendingFile && !uploading) { handleCancelPending(); return; }
        if (!uploading) onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, pendingFile, uploading]); // eslint-disable-line

  const { icon: fileIcon, color: fileColor } = pendingFile
    ? getFileIcon(pendingFile.file.name)
    : { icon: null, color: null };

  return (
    <>
      <div className="od-drawer-backdrop" onClick={onClose} />
      <div className="od-drawer">
        <div className="od-drawer-header">
          <div className="od-drawer-title">
            <span className="od-drawer-icon">📁</span>
            <div>
              <div className="od-drawer-product-name">{line.product || '(unnamed)'}</div>
              <div className="od-drawer-product-meta">{line.qty} {line.unit}</div>
            </div>
          </div>
          <button className="od-drawer-close" onClick={onClose} title="Close">
            <FiX size={18} />
          </button>
        </div>

        <div className="od-drawer-body">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />

          {uploading ? (
            <div className="od-dropzone od-dropzone--uploading">
              <div className="od-dropzone-inner">
                <div className="od-dropzone-spinner" />
                <div className="od-dropzone-label">Uploading…</div>
              </div>
            </div>

          ) : pendingFile ? (
            <div className="od-confirm-panel">
              <div className="od-confirm-file-row">
                <span className="od-confirm-file-icon" style={{ color: fileColor }}>
                  {fileIcon}
                </span>
                <div className="od-confirm-file-info">
                  <div className="od-confirm-file-name" title={pendingFile.file.name}>
                    {pendingFile.file.name}
                  </div>
                  <div className="od-confirm-file-size">
                    {formatBytes(pendingFile.file.size)}
                  </div>
                </div>
              </div>

              <div className="od-confirm-version-row">
                {pendingFile.detecting ? (
                  <span className="od-confirm-detecting">Detecting version…</span>
                ) : pendingFile.version ? (
                  <>
                    <span className="od-confirm-version-label">Detected version</span>
                    <span className="od-file-version-badge">{pendingFile.version}</span>
                  </>
                ) : (
                  <span className="od-confirm-no-version">No version info detected</span>
                )}
              </div>

              <div className="od-confirm-actions">
                <button
                  className="od-confirm-btn od-confirm-btn--cancel"
                  onClick={handleCancelPending}
                  disabled={pendingFile.detecting}
                >
                  Cancel
                </button>
                <button
                  className="od-confirm-btn od-confirm-btn--upload"
                  onClick={handleConfirmUpload}
                  disabled={pendingFile.detecting}
                >
                  <FiUpload size={14} /> Upload
                </button>
              </div>
            </div>

          ) : (
            <div
              className={`od-dropzone${dragOver ? ' od-dropzone--active' : ''}`}
              onClick={() => inputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="od-dropzone-inner">
                <div className="od-dropzone-upload-icon"><FiUpload size={28} /></div>
                <div className="od-dropzone-label">
                  {dragOver ? 'Drop to upload' : 'Click or drag & drop to upload'}
                </div>
                <div className="od-dropzone-hint">CDR, PSD, AI, PDF, SVG, PNG, JPG, EPS…</div>
              </div>
            </div>
          )}

          {files.length > 0 ? (
            <div className="od-drawer-files">
              <div className="od-drawer-files-label">
                Uploaded files <span className="od-drawer-files-count">{files.length}</span>
              </div>
              {files.map((f) => {
                const { icon, color } = getFileIcon(f.name);
                return (
                  <div key={f.id} className="od-file-item">
                    <div className="od-file-icon" style={{ color }}>{icon}</div>
                    <div className="od-file-body">
                      <div className="od-file-name">{f.name}</div>
                      <div className="od-file-meta">
                        {formatBytes(f.size)}
                        {f.uploadedAt && ` · ${new Date(f.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                        {f.fileVersion && (
                          <span className="od-file-version-badge">{f.fileVersion}</span>
                        )}
                      </div>
                    </div>
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="od-file-action od-file-action--download"
                      title="Download"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FiDownload size={14} />
                    </a>
                    <button
                      className="od-file-action od-file-action--delete"
                      title="Remove"
                      onClick={() => onDelete(f.id)}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="od-drawer-no-files">No files uploaded yet for this product.</div>
          )}
        </div>
      </div>
    </>
  );
};



/* ─── Main Page ──────────────────────────────────────────────────────── */

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [designFiles, setDesignFiles] = useState([]);
  const [uploading, setUploading] = useState({});
  const [activeLineId, setActiveLineId] = useState(null);
  const [activeTerm, setActiveTerm] = useState(null);

  // Fetch active payment term once — needed for Production step in indicator
  useEffect(() => {
    API.get('/payment-terms/active')
      .then(({ data }) => setActiveTerm(data.term || null))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/sales-orders/${id}`);
      const loaded = data.order || data;
      setOrder(loaded);
      setDesignFiles(safeArray(loaded.designFiles));
    } catch {
      toast.error('Order not found');
      navigate('/admin/order-detail');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const persistFiles = async (updated) => {
    await API.put(`/sales-orders/${id}`, { designFiles: updated });
    setOrder((o) => ({ ...o, designFiles: updated }));
  };

  const handleUpload = async (lineId, productName, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (e.target) e.target.value = '';
    setUploading((u) => ({ ...u, [lineId]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/upload/design-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newFile = {
        id: `DF${Date.now()}`,
        productLineId: lineId,
        productName,
        name: file.name,
        url: data.url,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        fileVersion: data.fileVersion || null,
      };
      const updated = [...designFiles, newFile];
      setDesignFiles(updated);
      await persistFiles(updated);
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [lineId]: false }));
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Remove this file?')) return;
    const updated = designFiles.filter((f) => f.id !== fileId);
    setDesignFiles(updated);
    try {
      await persistFiles(updated);
      toast.success('File removed');
    } catch {
      setDesignFiles(designFiles);
      toast.error('Could not remove file');
    }
  };

  const fileCountFor = (lineId) => designFiles.filter((f) => f.productLineId === lineId).length;

  if (loading) return <div className="od-spinner">Loading…</div>;
  if (!order) return null;

  const items = safeArray(order.items);
  const activeItem = items.find((i) => i.id === activeLineId) || null;
  const addr = parseDeliveryAddress(order.deliveryAddress);
  const balanceDue = (order.grandTotal || 0) - (order.amountReceived || 0);

  const addrLines = addr
    ? [addr.line1, addr.line2, addr.city && `${addr.city}, ${addr.state}`, addr.pincode, addr.country]
        .filter(Boolean).join('\n')
    : order.companyAddress || '—';

  return (
    <div className="od-page">
      <div className="od-header">
        <div className="od-header-left">
          <button className="od-back-btn" onClick={() => navigate('/admin/order-detail')}>
            <FiArrowLeft size={15} /> Direct Orders
          </button>
          <span className="od-header-sep">/</span>
          <span className="od-order-num">{order.orderNumber}</span>
          <StatusBadge status={order.status} />
          <OrderTrackingIndicator order={order} activeTerm={activeTerm} />
        </div>
        <button className="od-edit-btn" onClick={() => navigate(`/admin/sales-orders/${id}`)}>
          <FiEdit2 size={14} /> Edit
        </button>
      </div>

      <div className="od-two-col">
        <div className="od-card">
          <div className="od-card-title">Customer &amp; delivery address</div>
          <div className="od-customer-name">{order.customerName}</div>
          {order.companyName && (
            <div className="od-muted" style={{ fontSize: '0.82rem', marginBottom: 4 }}>
              {order.companyName}
            </div>
          )}
          <div className="od-muted" style={{ fontSize: '0.82rem', marginBottom: 12 }}>
            {[order.customerPhone, order.customerEmail].filter(Boolean).join(' · ')}
          </div>
          {order.gstNo && (
            <div className="od-muted" style={{ fontSize: '0.8rem', marginBottom: 12 }}>
              GST: {order.gstNo}
            </div>
          )}
          <div className="od-address-box">
            <div className="od-address-label">Delivery address</div>
            <div className="od-address-text">{addrLines}</div>
          </div>
        </div>

        <div className="od-card">
          <div className="od-card-title">Order meta</div>
          {[
            ['Order date', fmtDate(order.orderDate)],
            ['Delivery date', fmtDate(order.deliveryDate) || '—'],
            ['Delivery type', order.deliveryType === 'delivery' ? 'Courier / Delivery' : 'Pickup'],
            ['Payment type', order.paymentType === 'credit' ? 'Credit' : 'Cash'],
            order.dueDate ? ['Due date', fmtDate(order.dueDate)] : null,
            ['Balance due', <span key="b" style={{ color: balanceDue > 0 ? '#ef4444' : '#059669', fontWeight: 700 }}>{money(balanceDue)}</span>],
            order.notes ? ['Notes', order.notes] : null,
          ]
            .filter(Boolean)
            .map(([k, v]) => (
              <div key={k} className="od-meta-row">
                <span className="od-meta-label">{k}</span>
                <span className="od-meta-value">{v}</span>
              </div>
            ))}
        </div>
      </div>

      <div className="od-section">
        <div className="od-section-title">Products</div>
        <div className="od-table-wrap">
          <table className="od-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Unit</th>
                <th style={{ textAlign: 'right' }}>Price</th>
                <th style={{ textAlign: 'right' }}>Tax%</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((line) => {
                const base = (Number(line.qty) || 0) * (Number(line.price) || 0);
                const tax = base * ((Number(line.taxPercent) || 0) / 100);
                return (
                  <tr key={line.id}>
                    <td>
                      <span
                        className="od-product-name-link"
                        onClick={() => setActiveLineId(line.id)}
                        title="Click to upload / view design files"
                      >
                        {line.product || '—'}
                        {(() => { const c = fileCountFor(line.id); return c > 0 ? <span className="od-file-count-badge" style={{ marginLeft: 7 }}>{c} file{c !== 1 ? 's' : ''}</span> : null; })()}
                      </span>
                      {line.description && (
                        <div className="od-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
                          {line.description}
                        </div>
                      )}
                    </td>
                    <td>{line.qty}</td>
                    <td>{line.unit}</td>
                    <td style={{ textAlign: 'right' }}>{money(line.price)}</td>
                    <td style={{ textAlign: 'right' }}>{line.taxPercent}%</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(base + tax)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {activeLineId && activeItem && (
        <DesignFilesDrawer
          line={activeItem}
          designFiles={designFiles}
          onClose={() => setActiveLineId(null)}
          onUpload={handleUpload}
          onDelete={handleDelete}
          uploading={!!uploading[activeLineId]}
        />
      )}
    </div>
  );
};

export default OrderDetail;